'use client';

import { useState, useEffect } from 'react';
import { ImageProps, Skeleton, Box } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import { MantineNextImage } from './MantineNextImage';

interface LazyImageProps extends ImageProps {
    src: string;
    alt: string;
    threshold?: number;
    rootMargin?: string;
    skeletonHeight?: number | string;
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

export function LazyImage({
    src,
    alt,
    threshold = 0.1,
    rootMargin = '200px',
    skeletonHeight,
    style,
    onLoad,
    ...props
}: LazyImageProps) {
    const [loaded, setLoaded] = useState(false);
    const { ref, entry } = useIntersection({
        threshold,
        rootMargin,
    });

    const isVisible = !!entry?.isIntersecting;
    const [shouldLoad, setShouldLoad] = useState(false);

    useEffect(() => {
        if (isVisible && !shouldLoad) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShouldLoad(true);
        }
    }, [isVisible, shouldLoad]);

    return (
        <Box ref={ref} style={{ position: 'relative', ...style }}>
            {!loaded && (
                <Skeleton
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    height={skeletonHeight || (props.h as any) || '100%'}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    width={(props.w as any) || '100%'}
                    style={{ position: 'absolute', inset: 0, zIndex: 1 }}
                />
            )}
            {shouldLoad && (
                <MantineNextImage
                    src={src}
                    alt={alt}
                    {...props}
                    onLoad={(e) => {
                        setLoaded(true);
                        onLoad?.(e);
                    }}
                    style={{
                        ...style,
                        opacity: loaded ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                />
            )}
        </Box>
    );
}
